import { useEffect, useRef, useState } from "react";
import { api, ArtifactExpiredError } from "../api";
import { ExpiredPlaceholder } from "./ExpiredPlaceholder";

interface Props {
  jobId: string;
}

type State =
  | { kind: "loading" }
  | { kind: "expired"; error: ArtifactExpiredError }
  | { kind: "error"; message: string }
  | { kind: "rendering"; source: string }
  | { kind: "ready"; svg: string }
  | { kind: "render-failed"; source: string; reason: string };

// Lazy-init the mermaid module across mounts so the Flow tab only pulls
// the ~250 KB chunk once per session.
let mermaidPromise: Promise<MermaidApi> | null = null;
interface MermaidApi {
  render: (id: string, source: string) => Promise<{ svg: string }>;
}
function loadMermaid(): Promise<MermaidApi> {
  if (mermaidPromise) return mermaidPromise;
  mermaidPromise = import("mermaid").then((mod) => {
    const m = mod.default;
    m.initialize({ startOnLoad: false, theme: "dark", securityLevel: "strict" });
    return m as unknown as MermaidApi;
  });
  return mermaidPromise;
}

export function FlowTab({ jobId }: Props) {
  const [state, setState] = useState<State>({ kind: "loading" });
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    let cancelled = false;
    setState({ kind: "loading" });
    api
      .artifactFlow(jobId)
      .then(async (source) => {
        if (cancelled) return;
        setState({ kind: "rendering", source });
        try {
          const mermaid = await loadMermaid();
          const id = `flow-${jobId.slice(0, 8)}-${Date.now()}`;
          const { svg } = await mermaid.render(id, source);
          if (cancelled) return;
          setState({ kind: "ready", svg });
        } catch (e) {
          if (cancelled) return;
          setState({
            kind: "render-failed",
            source,
            reason: e instanceof Error ? e.message : String(e),
          });
        }
      })
      .catch((e) => {
        if (cancelled) return;
        if (e instanceof ArtifactExpiredError) {
          setState({ kind: "expired", error: e });
        } else {
          setState({
            kind: "error",
            message: e instanceof Error ? e.message : String(e),
          });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [jobId]);

  if (state.kind === "loading") return <p className="muted">Loading flow diagram…</p>;
  if (state.kind === "rendering") return <p className="muted">Rendering Mermaid…</p>;
  if (state.kind === "expired") return <ExpiredPlaceholder error={state.error} />;
  if (state.kind === "error") return <div className="error">{state.message}</div>;

  if (state.kind === "ready") {
    return (
      <div
        ref={containerRef}
        className="flow-svg"
        dangerouslySetInnerHTML={{ __html: state.svg }}
      />
    );
  }

  // render-failed
  return (
    <div>
      <p className="muted">
        Couldn't render the flow diagram ({state.reason}). Raw source below.
      </p>
      <pre className="flow-source">{state.source}</pre>
    </div>
  );
}
